/**
 * New business report assembly. Pure: stamps + adjustments + config in, the
 * report's data shape out. Shared by the BFF (page), the reports-scheduler
 * Function (daily email) and the My Reports preview, so the three can never
 * disagree (the #1274 anti-drift rule).
 *
 * Rules, all Sasha's (9/27):
 *   - A deal lands in its EFFECTIVE month; its sold month is shown beside it.
 *   - The value on a stamp is what was stamped, or its override. Nothing here
 *     re-runs the grouper: a weight change in config never restates history.
 *   - An adjustment lands in ITS OWN month, against the family of its deal.
 *   - Unclassified deals count (at the configured weight) and are listed so
 *     somebody fixes the opportunity.
 */

import type { NewBusinessFamily, NewBusinessValue } from '../../entities/admin-config/model/types';
import type {
  NewBusinessAdjustment,
  NewBusinessHistoryDetail,
  NewBusinessHistoryFigures,
  NewBusinessHistorySnapshot,
  NewBusinessStamp,
} from './types';
import { orderedGroupCategories, resolveBucket, resolveGroupCategory, UNCLASSIFIED_KEY } from './classify';

export interface FamilyCell {
  groups: number;
  actualLives: number;
  creditedLives: number;
}

/** A group count: deals, and deals times their category weight. */
export interface GroupCountCell {
  groups: number;
  weightedGroups: number;
}

/** One row of the weighted group count for the sales year. */
export interface NewBusinessGroupCategoryRow {
  key: string;
  label: string;
  weight: number;
  groups: number;
  weightedGroups: number;
  /** Share of the year's weighted groups, one decimal. */
  sharePct: number;
}

export interface NewBusinessMonthRow {
  /** YYYY-MM */
  month: string;
  /** "Feb 2026" */
  label: string;
  tpa: FamilyCell;
  rx: FamilyCell;
  mec: FamilyCell;
  vcs: FamilyCell;
  onsite: FamilyCell;
  other: FamilyCell;
  /** Families plus this month's adjustments. */
  total: FamilyCell;
  /** Deals SOLD in this calendar month, whatever their effective date. */
  sold: FamilyCell;
  /** Net credited-lives delta from adjustments dated in this month. */
  adjustmentCredited: number;
  /** Group count by group category key; every category in the table has an entry. */
  groupsByCategory: Record<string, GroupCountCell>;
  /** Sum of the month's weighted groups. */
  weightedGroups: number;
}

export interface NewBusinessProgramRow {
  program: string;
  groups: number;
  actualLives: number;
  creditedLives: number;
  /** Share of the pharmacy family's credited lives, one decimal. */
  sharePct: number;
}

/**
 * Where a history row's figures come from (Sasha 10/05):
 *   'entered'   a snapshot typed in by hand (backfill)
 *   'closed'    a snapshot frozen from the stamps after the year ended
 *   'live'      the current sales year, from the stamps, no snapshot yet
 *   'computed'  a past year with no snapshot yet, from the stamps; it moves
 *               until somebody closes it or enters its totals
 */
export type NewBusinessHistorySource = 'entered' | 'closed' | 'live' | 'computed';

export interface NewBusinessHistoryRow extends NewBusinessHistoryFigures {
  salesYear: number;
  label: string;
  source: NewBusinessHistorySource;
  /** The snapshot shown (highest version), null for live and computed rows. */
  snapshot: NewBusinessHistorySnapshot | null;
  /** How many snapshot versions the year has. */
  versions: number;
  current: boolean;
  /**
   * What the stamps say today, whatever the row shows. On a closed row a
   * difference is a late close, an override or an adjustment that landed
   * after the year was frozen.
   */
  live: NewBusinessHistoryFigures;
  /** Closed row whose live credited lives no longer match the snapshot. */
  drifted: boolean;
}

export interface NewBusinessDealRow {
  opportunityId: string;
  groupName: string;
  accountName: string | null;
  brokerAgency: string | null;
  salesperson: string | null;
  effectiveDate: string | null;
  soldDate: string | null;
  /** YYYY-MM of the effective date, or '' when there is none. */
  month: string;
  actualLives: number | null;
  /** Stamped credit, or the override. Adjustments are NOT folded in. */
  creditedLives: number;
  bucketKey: string;
  bucketLabel: string;
  family: NewBusinessFamily;
  program: string | null;
  /** Group count row this deal lands in, from the current config. */
  groupCategoryKey: string;
  groupCategoryLabel: string;
  /** How many groups this deal counts as. */
  groupWeight: number;
  /** The bucket's weight (stamped, or the override bucket's). */
  weight: number;
  /**
   * What the deal is actually credited at: creditedLives / actualLives. Equals
   * `weight` for an untouched stamp; a flat override of 100 on 1,400 actual
   * lives reads 0.0714 here while `weight` still says the bucket's 0.5.
   */
  rate: number;
  overridden: boolean;
  overrideReason: string | null;
  /** Latest admin re-rate, if any. */
  reratedAt: string | null;
  unclassified: boolean;
  /** Net credited-lives delta from this deal's adjustments (any month). */
  adjustmentCredited: number;
  signals: string[];
  provenance: NewBusinessStamp['provenance'];
}

export interface NewBusinessReportData {
  salesYear: number;
  /** "Feb 2026 - Jan 2027" */
  salesYearLabel: string;
  /** YYYY-MM-DD */
  salesYearStart: string;
  salesYearEnd: string;
  asOf: string;
  goal: number | null;
  totals: {
    groups: number;
    actualLives: number;
    creditedLives: number;
    adjustmentCredited: number;
    /** Every deal times its group category weight. */
    weightedGroups: number;
    pctOfGoal: number | null;
    remaining: number | null;
  };
  families: { tpa: FamilyCell; rx: FamilyCell; mec: FamilyCell; vcs: FamilyCell; onsite: FamilyCell; other: FamilyCell };
  /** Share of credited lives by family, one decimal, summing to about 100. */
  splitPct: { tpa: number; rx: number; mec: number; vcs: number; onsite: number; other: number };
  months: NewBusinessMonthRow[];
  /** Pharmacy family broken out by program, largest first. */
  programs: NewBusinessProgramRow[];
  /**
   * Weighted group count, in the configured category order. A category a
   * bucket points at but the table lacks is appended (weight 1).
   */
  groupCategories: NewBusinessGroupCategoryRow[];
  history: NewBusinessHistoryRow[];
  /** Every deal in the sales year, by effective date then group. */
  deals: NewBusinessDealRow[];
  unclassified: NewBusinessDealRow[];
  /** Adjustments dated inside the sales year. */
  adjustments: NewBusinessAdjustment[];
}

export interface AssembleNewBusinessInput {
  /** ALL stamps, every year (history needs them). */
  stamps: NewBusinessStamp[];
  /** ALL adjustments. */
  adjustments: NewBusinessAdjustment[];
  /** Every history snapshot version, every year. Absent: history is all live/computed. */
  history?: NewBusinessHistorySnapshot[];
  config: NewBusinessValue;
  /** Sales year by START year. */
  salesYear: number;
  /** 1-based start month of the sales year (sales.salesYear). */
  startMonth: number;
  /** ISO datetime, for the header. */
  asOf: string;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const emptyCell = (): FamilyCell => ({ groups: 0, actualLives: 0, creditedLives: 0 });

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** YYYY-MM from any ISO-ish date string, or '' */
export function monthOf(value: string | null | undefined): string {
  if (!value) return '';
  const ym = value.slice(0, 7);
  return /^\d{4}-\d{2}$/.test(ym) ? ym : '';
}

/** "Feb 2026" from YYYY-MM */
export function monthLabelOf(ym: string): string {
  const [y, m] = ym.split('-');
  const idx = Number(m) - 1;
  const abbr = MONTH_ABBR[idx];
  return abbr && y ? abbr + ' ' + y : ym;
}

/** Sales year (by start year) of a YYYY-MM or ISO date; null when unparseable. */
export function salesYearOf(value: string | null | undefined, startMonth: number): number | null {
  const ym = monthOf(value);
  if (!ym) return null;
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return m >= startMonth ? y : y - 1;
}

/** The twelve YYYY-MM keys of a sales year, in order. */
export function salesYearMonthKeys(salesYear: number, startMonth: number): string[] {
  const keys: string[] = [];
  for (let i = 0; i < 12; i++) {
    const idx = startMonth - 1 + i;
    const y = salesYear + Math.floor(idx / 12);
    const m = (idx % 12) + 1;
    keys.push(y + '-' + String(m).padStart(2, '0'));
  }
  return keys;
}

export function salesYearBounds(salesYear: number, startMonth: number): { start: string; end: string; label: string } {
  const keys = salesYearMonthKeys(salesYear, startMonth);
  const first = keys[0]!;
  const last = keys[11]!;
  const [ly, lm] = last.split('-').map(Number) as [number, number];
  const lastDay = new Date(ly, lm, 0).getDate();
  return {
    start: first + '-01',
    end: last + '-' + String(lastDay).padStart(2, '0'),
    label: monthLabelOf(first) + ' - ' + monthLabelOf(last),
  };
}

/**
 * The values a stamp contributes: its override when present, else what was
 * stamped. Bucket display comes from the CURRENT config so a relabel shows
 * everywhere, falling back to the stamped label when the key is gone.
 */
export function effectiveValues(stamp: NewBusinessStamp, config: NewBusinessValue): {
  bucketKey: string;
  bucketLabel: string;
  family: NewBusinessFamily;
  program: string | null;
  weight: number;
  creditedLives: number;
  overridden: boolean;
  unclassified: boolean;
} {
  const bucketKey = stamp.override?.bucketKey ?? stamp.bucketKey;
  const resolved = resolveBucket(bucketKey, config);
  const sameAsStamped = bucketKey === stamp.bucketKey;
  return {
    bucketKey,
    bucketLabel: resolved?.label ?? (sameAsStamped ? stamp.bucketLabel : bucketKey),
    family: resolved?.family ?? stamp.family,
    program: resolved?.program ?? (sameAsStamped ? stamp.program : null),
    weight: resolved?.weight ?? stamp.weight,
    creditedLives: stamp.override?.creditedLives ?? stamp.creditedLives,
    overridden: stamp.override !== null,
    unclassified: bucketKey === UNCLASSIFIED_KEY,
  };
}

function toDealRow(
  stamp: NewBusinessStamp,
  config: NewBusinessValue,
  adjustmentCredited: number,
): NewBusinessDealRow {
  const v = effectiveValues(stamp, config);
  const gc = resolveGroupCategory(v.bucketKey, config);
  return {
    opportunityId: stamp.opportunityId,
    groupName: stamp.groupName,
    accountName: stamp.accountName,
    brokerAgency: stamp.brokerAgency,
    salesperson: stamp.salesperson,
    effectiveDate: stamp.effectiveDate,
    soldDate: stamp.soldDate,
    month: monthOf(stamp.effectiveDate),
    actualLives: stamp.actualLives,
    creditedLives: v.creditedLives,
    bucketKey: v.bucketKey,
    bucketLabel: v.bucketLabel,
    family: v.family,
    program: v.program,
    groupCategoryKey: gc.key,
    groupCategoryLabel: gc.label,
    groupWeight: gc.weight,
    weight: v.weight,
    rate:
      stamp.actualLives != null && stamp.actualLives > 0
        ? Math.round((v.creditedLives / stamp.actualLives) * 10000) / 10000
        : v.weight,
    overridden: v.overridden,
    overrideReason: stamp.override?.reason ?? null,
    reratedAt: stamp.reratedAt ?? null,
    unclassified: v.unclassified,
    adjustmentCredited,
    signals: stamp.signals,
    provenance: stamp.provenance,
  };
}

function addDeal(cell: FamilyCell, row: NewBusinessDealRow): void {
  cell.groups += 1;
  cell.actualLives = round2(cell.actualLives + (row.actualLives ?? 0));
  cell.creditedLives = round2(cell.creditedLives + row.creditedLives);
}

function addAdjustment(cell: FamilyCell, a: NewBusinessAdjustment): void {
  cell.actualLives = round2(cell.actualLives + a.deltaActualLives);
  cell.creditedLives = round2(cell.creditedLives + a.deltaCreditedLives);
}

function sumCells(cells: FamilyCell[]): FamilyCell {
  const out = emptyCell();
  for (const c of cells) {
    out.groups += c.groups;
    out.actualLives = round2(out.actualLives + c.actualLives);
    out.creditedLives = round2(out.creditedLives + c.creditedLives);
  }
  return out;
}

function addGroup(cells: Record<string, GroupCountCell>, row: NewBusinessDealRow): void {
  const cell = cells[row.groupCategoryKey] ?? { groups: 0, weightedGroups: 0 };
  cell.groups += 1;
  cell.weightedGroups = round2(cell.weightedGroups + row.groupWeight);
  cells[row.groupCategoryKey] = cell;
}

function weightedSum(rows: readonly NewBusinessDealRow[]): number {
  return round2(rows.reduce((n, r) => n + r.groupWeight, 0));
}

/** A sales year's figures from the stamps and adjustments as they stand now. */
function liveYearFigures(
  y: number,
  input: AssembleNewBusinessInput,
  allRows: readonly NewBusinessDealRow[],
  rowByOpp: ReadonlyMap<string, NewBusinessDealRow>,
  rowSalesYear: (r: NewBusinessDealRow) => number | null,
): NewBusinessHistoryFigures {
  const cells = { tpa: emptyCell(), rx: emptyCell(), mec: emptyCell(), vcs: emptyCell(), onsite: emptyCell(), other: emptyCell() };
  const yearRows = allRows.filter((r) => rowSalesYear(r) === y);
  for (const r of yearRows) addDeal(cells[r.family], r);
  for (const a of input.adjustments) {
    if (salesYearOf(a.adjustmentDate, input.startMonth) !== y) continue;
    addAdjustment(cells[rowByOpp.get(a.opportunityId)?.family ?? 'other'], a);
  }
  const total = sumCells([cells.tpa, cells.rx, cells.mec, cells.vcs, cells.onsite, cells.other]);
  return {
    groups: total.groups,
    weightedGroups: weightedSum(yearRows),
    actualLives: total.actualLives,
    creditedLives: total.creditedLives,
    tpaCreditedLives: cells.tpa.creditedLives,
    rxCreditedLives: cells.rx.creditedLives,
    mecCreditedLives: cells.mec.creditedLives,
    vcsCreditedLives: cells.vcs.creditedLives,
    onsiteCreditedLives: cells.onsite.creditedLives,
    otherCreditedLives: cells.other.creditedLives,
    goal: input.config.goals.find((g) => g.salesYear === y)?.creditedLives ?? null,
  };
}

function byEffectiveThenGroup(a: NewBusinessDealRow, b: NewBusinessDealRow): number {
  const ea = a.effectiveDate ?? '';
  const eb = b.effectiveDate ?? '';
  if (ea !== eb) return ea < eb ? -1 : 1;
  return a.groupName.localeCompare(b.groupName, undefined, { sensitivity: 'base' });
}

export function assembleNewBusinessData(input: AssembleNewBusinessInput): NewBusinessReportData {
  const { config, salesYear, startMonth } = input;
  const bounds = salesYearBounds(salesYear, startMonth);
  const monthKeys = salesYearMonthKeys(salesYear, startMonth);
  const categoryKeys = orderedGroupCategories(config).map((c) => c.key);

  // Adjustment totals per deal, and adjustments indexed by month.
  const adjByOpp = new Map<string, number>();
  for (const a of input.adjustments) {
    adjByOpp.set(a.opportunityId, round2((adjByOpp.get(a.opportunityId) ?? 0) + a.deltaCreditedLives));
  }

  // Every stamp as a row, keyed for adjustment family lookup.
  const allRows = input.stamps.map((s) => toDealRow(s, config, adjByOpp.get(s.opportunityId) ?? 0));
  const rowByOpp = new Map(allRows.map((r) => [r.opportunityId, r]));
  const rowSalesYear = (r: NewBusinessDealRow): number | null =>
    salesYearOf(r.effectiveDate, startMonth) ?? input.stamps.find((s) => s.opportunityId === r.opportunityId)?.salesYear ?? null;

  // ----- current sales year -----
  const deals = allRows.filter((r) => rowSalesYear(r) === salesYear).sort(byEffectiveThenGroup);
  const adjustments = input.adjustments
    .filter((a) => salesYearOf(a.adjustmentDate, startMonth) === salesYear)
    .sort((a, b) => (a.adjustmentDate < b.adjustmentDate ? -1 : a.adjustmentDate > b.adjustmentDate ? 1 : 0));

  const months: NewBusinessMonthRow[] = monthKeys.map((month) => ({
    month,
    label: monthLabelOf(month),
    tpa: emptyCell(),
    rx: emptyCell(),
    mec: emptyCell(),
    vcs: emptyCell(),
    onsite: emptyCell(),
    other: emptyCell(),
    total: emptyCell(),
    sold: emptyCell(),
    adjustmentCredited: 0,
    groupsByCategory: Object.fromEntries(categoryKeys.map((k) => [k, { groups: 0, weightedGroups: 0 }])),
    weightedGroups: 0,
  }));
  const monthByKey = new Map(months.map((m) => [m.month, m]));

  for (const r of deals) {
    const m = monthByKey.get(r.month);
    if (!m) continue;
    addDeal(m[r.family], r);
    addGroup(m.groupsByCategory, r);
    m.weightedGroups = round2(m.weightedGroups + r.groupWeight);
  }
  for (const a of adjustments) {
    const m = monthByKey.get(monthOf(a.adjustmentDate));
    const family = rowByOpp.get(a.opportunityId)?.family ?? 'other';
    if (!m) continue;
    addAdjustment(m[family], a);
    m.adjustmentCredited = round2(m.adjustmentCredited + a.deltaCreditedLives);
  }
  // Sold-in-month reads across every year: a January effective sold in
  // November belongs to this year's November momentum.
  for (const r of allRows) {
    const m = monthByKey.get(monthOf(r.soldDate));
    if (m) addDeal(m.sold, r);
  }
  for (const m of months) m.total = sumCells([m.tpa, m.rx, m.mec, m.vcs, m.onsite, m.other]);

  const families = {
    tpa: sumCells(months.map((m) => m.tpa)),
    rx: sumCells(months.map((m) => m.rx)),
    mec: sumCells(months.map((m) => m.mec)),
    vcs: sumCells(months.map((m) => m.vcs)),
    onsite: sumCells(months.map((m) => m.onsite)),
    other: sumCells(months.map((m) => m.other)),
  };
  const total = sumCells([families.tpa, families.rx, families.mec, families.vcs, families.onsite, families.other]);
  const adjustmentCredited = round2(adjustments.reduce((n, a) => n + a.deltaCreditedLives, 0));
  const goal = config.goals.find((g) => g.salesYear === salesYear)?.creditedLives ?? null;
  const pct = (part: number): number => (total.creditedLives > 0 ? round1((part / total.creditedLives) * 100) : 0);

  // ----- pharmacy by program -----
  const programMap = new Map<string, NewBusinessProgramRow>();
  for (const r of deals) {
    if (r.family !== 'rx') continue;
    const name = r.program ?? r.bucketLabel;
    const p = programMap.get(name) ?? { program: name, groups: 0, actualLives: 0, creditedLives: 0, sharePct: 0 };
    p.groups += 1;
    p.actualLives = round2(p.actualLives + (r.actualLives ?? 0));
    p.creditedLives = round2(p.creditedLives + r.creditedLives);
    programMap.set(name, p);
  }
  const rxCredited = families.rx.creditedLives;
  const programs = [...programMap.values()]
    .map((p) => ({ ...p, sharePct: rxCredited > 0 ? round1((p.creditedLives / rxCredited) * 100) : 0 }))
    .sort((a, b) => b.creditedLives - a.creditedLives || a.program.localeCompare(b.program));

  // ----- weighted group count -----
  const groupCells: Record<string, GroupCountCell> = {};
  for (const r of deals) addGroup(groupCells, r);
  const weightedGroups = weightedSum(deals);
  const categoryRows = new Map<string, NewBusinessGroupCategoryRow>();
  const addCategoryRow = (key: string, label: string, weight: number) => {
    const cell = groupCells[key] ?? { groups: 0, weightedGroups: 0 };
    categoryRows.set(key, {
      key,
      label,
      weight,
      groups: cell.groups,
      weightedGroups: cell.weightedGroups,
      sharePct: weightedGroups > 0 ? round1((cell.weightedGroups / weightedGroups) * 100) : 0,
    });
  };
  for (const c of orderedGroupCategories(config)) addCategoryRow(c.key, c.label, c.weight);
  for (const r of deals) {
    if (!categoryRows.has(r.groupCategoryKey)) addCategoryRow(r.groupCategoryKey, r.groupCategoryLabel, r.groupWeight);
  }

  // ----- history by sales year -----
  const years = new Set<number>([salesYear]);
  for (const r of allRows) {
    const y = rowSalesYear(r);
    if (y !== null) years.add(y);
  }
  for (const a of input.adjustments) {
    const y = salesYearOf(a.adjustmentDate, startMonth);
    if (y !== null) years.add(y);
  }
  for (const g of config.goals) years.add(g.salesYear);
  const snapshotsByYear = new Map<number, NewBusinessHistorySnapshot[]>();
  for (const s of input.history ?? []) {
    years.add(s.salesYear);
    const list = snapshotsByYear.get(s.salesYear) ?? [];
    list.push(s);
    snapshotsByYear.set(s.salesYear, list);
  }
  const history: NewBusinessHistoryRow[] = [...years]
    .sort((a, b) => a - b)
    .map((y) => {
      const live = liveYearFigures(y, input, allRows, rowByOpp, rowSalesYear);
      const versions = snapshotsByYear.get(y) ?? [];
      const snapshot = versions.reduce<NewBusinessHistorySnapshot | null>(
        (best, s) => (best === null || s.version > best.version ? s : best),
        null,
      );
      const current = y === salesYear;
      if (!snapshot) {
        return {
          salesYear: y,
          label: salesYearBounds(y, startMonth).label,
          source: current ? 'live' : 'computed',
          snapshot: null,
          versions: 0,
          current,
          ...live,
          live,
          drifted: false,
        };
      }
      const figures: NewBusinessHistoryFigures = {
        groups: snapshot.groups,
        weightedGroups: snapshot.weightedGroups,
        actualLives: snapshot.actualLives,
        creditedLives: snapshot.creditedLives,
        tpaCreditedLives: snapshot.tpaCreditedLives,
        rxCreditedLives: snapshot.rxCreditedLives,
        mecCreditedLives: snapshot.mecCreditedLives,
        vcsCreditedLives: snapshot.vcsCreditedLives,
        onsiteCreditedLives: snapshot.onsiteCreditedLives,
        otherCreditedLives: snapshot.otherCreditedLives,
        goal: snapshot.goal ?? live.goal,
      };
      return {
        salesYear: y,
        label: salesYearBounds(y, startMonth).label,
        source: snapshot.kind,
        snapshot,
        versions: versions.length,
        current,
        ...figures,
        live,
        drifted:
          snapshot.kind === 'closed' &&
          snapshot.creditedLives !== null &&
          round2(snapshot.creditedLives) !== round2(live.creditedLives ?? 0),
      };
    });

  return {
    salesYear,
    salesYearLabel: bounds.label,
    salesYearStart: bounds.start,
    salesYearEnd: bounds.end,
    asOf: input.asOf,
    goal,
    totals: {
      groups: total.groups,
      actualLives: total.actualLives,
      creditedLives: total.creditedLives,
      adjustmentCredited,
      weightedGroups,
      pctOfGoal: goal ? round1((total.creditedLives / goal) * 100) : null,
      remaining: goal ? round2(goal - total.creditedLives) : null,
    },
    families,
    splitPct: {
      tpa: pct(families.tpa.creditedLives),
      rx: pct(families.rx.creditedLives),
      mec: pct(families.mec.creditedLives),
      vcs: pct(families.vcs.creditedLives),
      onsite: pct(families.onsite.creditedLives),
      other: pct(families.other.creditedLives),
    },
    months,
    programs,
    groupCategories: [...categoryRows.values()],
    history,
    deals,
    unclassified: deals.filter((d) => d.unclassified),
    adjustments,
  };
}

// ---------------------------------------------------------------------------
// Deals by effective month: the layout Rose's workbook used and the one both
// the page and the email list deals in. One rule, one place.
// ---------------------------------------------------------------------------

export type NewBusinessDealLine =
  | { kind: 'deal'; deal: NewBusinessDealRow }
  | {
      /**
       * A month's deals in one bucket folded into a single line, the way Rose
       * wrote "CBIZ MEC/MVP - (9 Groups)": Michael reads the MEC block as a
       * block, and the sold date of each little group is not what he is
       * looking at (Sasha 9/27).
       */
      kind: 'rollup';
      key: string;
      bucketKey: string;
      bucketLabel: string;
      count: number;
      actual: number;
      credited: number;
      /** How many of the folded deals carry an override. */
      overridden: number;
      /** Net credited-lives delta from the folded deals' adjustments. */
      adjustments: number;
    };

export interface NewBusinessMonthDeals {
  /** YYYY-MM, or '' for deals with no effective date. */
  month: string;
  label: string;
  /** Every deal of the month, alphabetized by group. */
  deals: NewBusinessDealRow[];
  /** The rendered lines: deals, then the roll-up line last when one applies. */
  lines: NewBusinessDealLine[];
  actual: number;
  credited: number;
}

export function sortDealsByGroup(rows: readonly NewBusinessDealRow[]): NewBusinessDealRow[] {
  return [...rows].sort((a, b) => a.groupName.localeCompare(b.groupName, undefined, { sensitivity: 'base' }));
}

/**
 * Group deals under their effective month in sales-year order, alphabetized
 * within the month; months the sales year does not know (none expected) and
 * deals with no effective date follow, the latter labelled as such. When
 * `rollUpBucketKey` is given, that bucket's deals in each month fold into one
 * roll-up line placed after the others.
 */
export function groupDealsByMonth(
  deals: readonly NewBusinessDealRow[],
  months: readonly { month: string; label: string }[],
  rollUpBucketKey: string | null,
): NewBusinessMonthDeals[] {
  const byMonth = new Map<string, NewBusinessDealRow[]>();
  for (const d of deals) {
    const list = byMonth.get(d.month) ?? [];
    list.push(d);
    byMonth.set(d.month, list);
  }
  const build = (month: string, label: string, list: NewBusinessDealRow[]): NewBusinessMonthDeals => {
    const sorted = sortDealsByGroup(list);
    const lines: NewBusinessDealLine[] = [];
    const folded: NewBusinessDealRow[] = [];
    for (const d of sorted) {
      if (rollUpBucketKey !== null && d.bucketKey === rollUpBucketKey) folded.push(d);
      else lines.push({ kind: 'deal', deal: d });
    }
    if (folded.length > 0) {
      lines.push({
        kind: 'rollup',
        key: 'rollup-' + rollUpBucketKey + '-' + month,
        bucketKey: folded[0]!.bucketKey,
        bucketLabel: folded[0]!.bucketLabel,
        count: folded.length,
        actual: round2(folded.reduce((n, d) => n + (d.actualLives ?? 0), 0)),
        credited: round2(folded.reduce((n, d) => n + d.creditedLives, 0)),
        overridden: folded.filter((d) => d.overridden).length,
        adjustments: round2(folded.reduce((n, d) => n + d.adjustmentCredited, 0)),
      });
    }
    return {
      month,
      label,
      deals: sorted,
      lines,
      actual: round2(sorted.reduce((n, d) => n + (d.actualLives ?? 0), 0)),
      credited: round2(sorted.reduce((n, d) => n + d.creditedLives, 0)),
    };
  };
  const out: NewBusinessMonthDeals[] = [];
  const seen = new Set<string>();
  for (const m of months) {
    seen.add(m.month);
    const list = byMonth.get(m.month);
    if (list && list.length > 0) out.push(build(m.month, m.label, list));
  }
  const rest = [...byMonth.keys()].filter((k) => !seen.has(k) && k !== '').sort();
  for (const k of rest) out.push(build(k, monthLabelOf(k), byMonth.get(k)!));
  const undated = byMonth.get('');
  if (undated && undated.length > 0) out.push(build('', 'No effective date', undated));
  return out;
}

// ---------------------------------------------------------------------------
// Closing a sales year (Sasha 10/05): freeze what the stamps say now into a
// history snapshot. Pass the report assembled FOR that year.
// ---------------------------------------------------------------------------

export function closingFigures(data: NewBusinessReportData): {
  figures: NewBusinessHistoryFigures;
  detail: NewBusinessHistoryDetail;
} {
  return {
    figures: {
      groups: data.totals.groups,
      weightedGroups: data.totals.weightedGroups,
      actualLives: data.totals.actualLives,
      creditedLives: data.totals.creditedLives,
      tpaCreditedLives: data.families.tpa.creditedLives,
      rxCreditedLives: data.families.rx.creditedLives,
      mecCreditedLives: data.families.mec.creditedLives,
      vcsCreditedLives: data.families.vcs.creditedLives,
      onsiteCreditedLives: data.families.onsite.creditedLives,
      otherCreditedLives: data.families.other.creditedLives,
      goal: data.goal,
    },
    detail: {
      groupCategories: data.groupCategories.map(({ key, label, weight, groups, weightedGroups }) => ({
        key,
        label,
        weight,
        groups,
        weightedGroups,
      })),
      programs: data.programs.map(({ program, groups, actualLives, creditedLives }) => ({
        program,
        groups,
        actualLives,
        creditedLives,
      })),
    },
  };
}
